import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Stack,
  Button,
  ButtonGroup,
  ToggleButton,
  ToggleButtonGroup,
  Container,
  Typography,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  InputAdornment,
  Grid,
  Box,
  Divider,
  IconButton,
  Chip,
  Alert,
  Autocomplete,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  getFirestore,
  serverTimestamp,
} from 'firebase/firestore';
import firebaseApp from '../utils/firebaseConfig';
import { sortLanes, laneLabel } from '../utils/lanes';
import ImageDropUpload from '../components/forms/ImageDropUpload';
import { PERMISSIONS, hasPermission } from '../protectedRoute';

const db = getFirestore(firebaseApp);

const CATEGORY_TYPES = [
  { value: 'lane', label: 'Lane de Tiro' },
  { value: 'course', label: 'Curso' },
  { value: 'class', label: 'Clase' },
  { value: 'event', label: 'Evento' },
  { value: 'one-on-one', label: 'One on One' },
  { value: 'no-license-shooting', label: 'Tiro Sin Licencia' },
];

const SCHEDULE_TYPES = ['course', 'class', 'one-on-one', 'no-license-shooting'];
const DAYS_SHORT = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

export default function NewReservationCategoryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const presetType = searchParams.get('type');
  const canEdit = hasPermission(PERMISSIONS.EDIT_CATEGORIES);

  const [formData, setFormData] = useState({
    name: '',
    type: presetType || 'lane',
    description: '',
    durationMinutes: 60,
    priceMemberCents: 0,
    priceNonMemberCents: 0,
    maxParticipants: 1,
    imageUrl: '',
    active: true,
    sortOrder: 0,
    requiresLicense: false,
    // Curso privado: al reservar se exige idioma (Español/Inglés) y si es
    // licencia nueva o renovación. Las categorías con "privado" en el nombre
    // se tratan como privadas aunque este switch esté apagado.
    privateCourse: false,
    hiddenFromPublic: false,
    depositEnabled: false,
    depositCents: 0,
    depositMessage: '',
    // Ventana para que el CLIENTE cambie su cita él mismo (horas antes).
    // null = usar el default del sistema (2h range / 24h cursos).
    rescheduleHoursBefore: null,
    // Color de fondo de las celdas de este tipo de lane en el calendario del
    // dashboard (ej. Rifle vs Tiro sin licencia). Vacío = sin color.
    calendarColor: '',
  });

  const [loading, setLoading] = useState(false);

  // Horarios recurrentes (availability_rules)
  const [schedules, setSchedules] = useState([]);
  const [scheduleDays, setScheduleDays] = useState([]);
  // 'recurring' = se repite cada semana · 'fixed' = una sola fecha
  const [scheduleMode, setScheduleMode] = useState('recurring');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleStart, setScheduleStart] = useState('18:00');
  const [scheduleEnd, setScheduleEnd] = useState('19:00');
  const [scheduleCapacity, setScheduleCapacity] = useState(10);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleMsg, setScheduleMsg] = useState(null);

  // Ocultar fechas fijas ya pasadas (cursos privados que van acumulando
  // fechas; el cliente NO las borra porque son el récord de que el curso se
  // dio — pedido 2026-09-15). Los horarios recurrentes siempre se muestran.
  const [hidePastDates, setHidePastDates] = useState(true);
  const todayIsoStr = new Date().toISOString().split('T')[0];
  const isPastFixedDate = (sch) => Boolean(sch.date) && sch.date < todayIsoStr;
  const pastDatesCount = schedules.filter(isPastFixedDate).length;
  const visibleSchedules = hidePastDates ? schedules.filter((sch) => !isPastFixedDate(sch)) : schedules;

  // Edición inline de un horario existente (hora y capacidad)
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [editSchedule, setEditSchedule] = useState({ startTime: '', endTime: '', capacity: 10 });
  const [editScheduleSaving, setEditScheduleSaving] = useState(false);

  // Cierre de lanes durante esta categoría (lane_closures): esos lanes no
  // salen disponibles al público en esos días/horas.
  const [allLanes, setAllLanes] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [closures, setClosures] = useState([]);
  const [closureDays, setClosureDays] = useState([]);
  const [closureStart, setClosureStart] = useState('18:00');
  const [closureEnd, setClosureEnd] = useState('20:00');
  const [closureLanes, setClosureLanes] = useState([]);
  const [closureSaving, setClosureSaving] = useState(false);

  const loadLanes = async () => {
    try {
      // Las categorías se necesitan para etiquetar cada lane: hay un "Lane 1"
      // de Pistola y otro de Rifle, y sin la categoría se ven idénticos.
      const [lanesSnap, catsSnap] = await Promise.all([
        getDocs(collection(db, 'lanes')),
        getDocs(collection(db, 'reservation_categories')),
      ]);
      const cats = catsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const list = lanesSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((l) => l.active !== false);
      setAllCategories(cats);
      setAllLanes(sortLanes(list, cats));
    } catch (e) { console.error(e); }
  };

  const loadClosures = async () => {
    try {
      const snap = await getDocs(query(collection(db, 'lane_closures'), where('categoryId', '==', editId)));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.dayOfWeek - b.dayOfWeek) || (a.startTime || '').localeCompare(b.startTime || ''));
      setClosures(list);
    } catch (e) { console.error(e); }
  };

  const handleAddClosure = async () => {
    if (closureDays.length === 0 || closureLanes.length === 0) {
      setScheduleMsg({ type: 'error', text: 'Selecciona al menos un día y un lane para el cierre.' });
      return;
    }
    setClosureSaving(true);
    try {
      await Promise.all(closureDays.map((dow) =>
        setDoc(doc(collection(db, 'lane_closures')), {
          categoryId: editId,
          dayOfWeek: Number(dow),
          startTime: closureStart,
          endTime: closureEnd,
          laneIds: closureLanes.map((l) => l.id),
          active: true,
          createdAt: serverTimestamp(),
        })
      ));
      setClosureDays([]);
      setClosureLanes([]);
      setScheduleMsg({ type: 'success', text: 'Cierre(s) de lanes añadidos.' });
      loadClosures();
    } catch (e) {
      console.error(e);
      setScheduleMsg({ type: 'error', text: 'Error guardando el cierre.' });
    }
    setClosureSaving(false);
  };

  const handleDeleteClosure = async (id) => {
    if (!window.confirm('¿Eliminar este cierre de lanes?')) return;
    try {
      await deleteDoc(doc(db, 'lane_closures', id));
      loadClosures();
    } catch (e) { console.error(e); }
  };

  const handleToggleClosure = async (c) => {
    try {
      await updateDoc(doc(db, 'lane_closures', c.id), { active: !c.active });
      loadClosures();
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (editId) {
      loadCategory();
      loadSchedules();
      loadClosures();
      loadLanes();
    }
  }, [editId]);

  const loadCategory = async () => {
    try {
      const docRef = doc(db, 'reservation_categories', editId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setFormData({ ...formData, ...docSnap.data() });
      }
    } catch (error) {
      console.error('Error loading category:', error);
    }
  };

  const loadSchedules = async () => {
    try {
      const q = query(collection(db, 'availability_rules'), where('categoryId', '==', editId));
      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Las de fecha fija primero (son puntuales), luego las recurrentes.
      list.sort((a, b) => {
        if (a.date && b.date) return a.date.localeCompare(b.date);
        if (a.date) return -1;
        if (b.date) return 1;
        return (a.dayOfWeek - b.dayOfWeek) || (a.startTime || '').localeCompare(b.startTime || '');
      });
      setSchedules(list);
    } catch (e) {
      console.error('Error loading schedules:', e);
    }
  };

  const handleAddSchedule = async () => {
    // Modo "fecha fija": un curso que se da UNA vez (ej: curso privado de
    // renovación). Se guarda con date y sin dayOfWeek para que no se repita.
    if (scheduleMode === 'fixed') {
      if (!scheduleDate) {
        setScheduleMsg({ type: 'error', text: 'Escoge la fecha del curso.' });
        return;
      }
    } else if (scheduleDays.length === 0) {
      setScheduleMsg({ type: 'error', text: 'Selecciona al menos un día.' });
      return;
    }
    if (!scheduleStart || !scheduleEnd) {
      setScheduleMsg({ type: 'error', text: 'Hora inicio y fin requeridas.' });
      return;
    }
    setScheduleSaving(true);
    setScheduleMsg(null);
    try {
      const base = {
        categoryId: editId,
        startTime: scheduleStart,
        endTime: scheduleEnd,
        slotDurationMinutes: Number(formData.durationMinutes) || 60,
        maxConcurrentBookings: Number(scheduleCapacity) || 10,
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      if (scheduleMode === 'fixed') {
        await setDoc(doc(collection(db, 'availability_rules')), {
          ...base,
          date: scheduleDate,
          dayOfWeek: null,
        });
      } else {
        await Promise.all(
          scheduleDays.map((dow) =>
            setDoc(doc(collection(db, 'availability_rules')), {
              ...base,
              dayOfWeek: Number(dow),
            })
          )
        );
      }
      setScheduleDays([]);
      setScheduleDate('');
      setScheduleMsg({ type: 'success', text: 'Horario(s) añadidos.' });
      loadSchedules();
    } catch (e) {
      console.error('Error creating schedule:', e);
      setScheduleMsg({ type: 'error', text: 'Error guardando el horario.' });
    }
    setScheduleSaving(false);
  };

  const handleDeleteSchedule = async (id) => {
    if (!window.confirm('¿Eliminar este horario?')) return;
    try {
      await deleteDoc(doc(db, 'availability_rules', id));
      loadSchedules();
    } catch (e) { console.error(e); }
  };

  const handleToggleScheduleActive = async (rule) => {
    try {
      await updateDoc(doc(db, 'availability_rules', rule.id), { active: !rule.active });
      loadSchedules();
    } catch (e) { console.error(e); }
  };

  const startEditSchedule = (rule) => {
    setEditingScheduleId(rule.id);
    setEditSchedule({
      startTime: rule.startTime || '',
      endTime: rule.endTime || '',
      capacity: rule.maxConcurrentBookings || 10,
    });
    setScheduleMsg(null);
  };

  // Guarda hora/capacidad del horario. Si cambió la hora, mueve también las
  // reservaciones futuras de ese día/hora para no perder a los inscritos.
  const handleSaveScheduleEdit = async (rule) => {
    if (!editSchedule.startTime || !editSchedule.endTime) {
      setScheduleMsg({ type: 'error', text: 'Hora inicio y fin requeridas.' });
      return;
    }
    setEditScheduleSaving(true);
    try {
      const timeChanged = editSchedule.startTime !== rule.startTime || editSchedule.endTime !== rule.endTime;
      let toMove = [];
      if (timeChanged) {
        const todayISO = new Date().toISOString().slice(0, 10);
        const snap = await getDocs(query(
          collection(db, 'reservations'),
          where('categoryId', '==', editId),
          where('startTime', '==', rule.startTime),
        ));
        toMove = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((r) => r.date >= todayISO)
          .filter((r) => ['confirmed', 'pending_payment'].includes(r.status))
          // Regla de fecha fija: solo esa fecha. Recurrente: por día de semana.
          .filter((r) => (rule.date
            ? r.date === rule.date
            : new Date(`${r.date}T12:00:00`).getDay() === rule.dayOfWeek));
        if (toMove.length > 0) {
          const ok = window.confirm(
            `Hay ${toMove.length} reservación(es) futuras en este horario. ` +
            `Se moverán a ${editSchedule.startTime} – ${editSchedule.endTime}. ¿Continuar?`
          );
          if (!ok) { setEditScheduleSaving(false); return; }
        }
      }
      await updateDoc(doc(db, 'availability_rules', rule.id), {
        startTime: editSchedule.startTime,
        endTime: editSchedule.endTime,
        maxConcurrentBookings: Number(editSchedule.capacity) || 1,
        updatedAt: serverTimestamp(),
      });
      await Promise.all(toMove.map((r) => updateDoc(doc(db, 'reservations', r.id), {
        startTime: editSchedule.startTime,
        endTime: editSchedule.endTime,
        updatedAt: serverTimestamp(),
      })));
      setEditingScheduleId(null);
      setScheduleMsg({
        type: 'success',
        text: toMove.length > 0
          ? `Horario actualizado. ${toMove.length} reservación(es) movidas a la nueva hora.`
          : 'Horario actualizado.',
      });
      loadSchedules();
    } catch (e) {
      console.error('Error updating schedule:', e);
      setScheduleMsg({ type: 'error', text: 'Error actualizando el horario.' });
    }
    setEditScheduleSaving(false);
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [field]: value });
  };

  const handleNumberChange = (field) => (e) => {
    setFormData({ ...formData, [field]: Number(e.target.value) || 0 });
  };

  const handlePriceChange = (field) => (e) => {
    const dollars = parseFloat(e.target.value) || 0;
    setFormData({ ...formData, [field]: Math.round(dollars * 100) });
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.type) {
      alert('Name and type are required');
      return;
    }
    setLoading(true);
    try {
      if (editId) {
        await updateDoc(doc(db, 'reservation_categories', editId), {
          ...formData,
          updatedAt: serverTimestamp(),
        });
        alert('Category updated successfully');
      } else {
        const newRef = doc(collection(db, 'reservation_categories'));
        await setDoc(newRef, {
          ...formData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        alert('Category created successfully');
      }
      navigate('/dashboard/reservation-categories');
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Error saving category');
    }
    setLoading(false);
  };

  return (
    <>
      <Helmet>
        <title>{editId ? 'Edit' : 'New'} Reservation Category | BBTC Dashboard</title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            {editId ? 'Edit' : 'New'} Reservation Category
          </Typography>
        </Stack>

        {!canEdit && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Tienes permiso de <b>Ver Categorías</b> pero no de editarlas. Los campos
            están en modo solo lectura.
          </Alert>
        )}

        <Card sx={{ p: 3 }} component="fieldset" disabled={!canEdit} style={{ border: 0, minWidth: 0 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={handleChange('name')}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Type"
                value={formData.type}
                onChange={handleChange('type')}
              >
                {CATEGORY_TYPES.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={formData.description}
                onChange={handleChange('description')}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Duration (minutes)"
                value={formData.durationMinutes}
                onChange={handleNumberChange('durationMinutes')}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Member Price"
                value={(formData.priceMemberCents / 100).toFixed(2)}
                onChange={handlePriceChange('priceMemberCents')}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Non-Member Price"
                value={(formData.priceNonMemberCents / 100).toFixed(2)}
                onChange={handlePriceChange('priceNonMemberCents')}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Max Participants"
                value={formData.maxParticipants}
                onChange={handleNumberChange('maxParticipants')}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Sort Order"
                value={formData.sortOrder}
                onChange={handleNumberChange('sortOrder')}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  component="input"
                  type="color"
                  value={formData.calendarColor || '#ffffff'}
                  onChange={(e) => setFormData({ ...formData, calendarColor: e.target.value })}
                  sx={{ width: 56, height: 56, p: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', cursor: 'pointer' }}
                />
                <TextField
                  fullWidth
                  label="Color en calendario"
                  placeholder="#e3f2fd"
                  value={formData.calendarColor || ''}
                  onChange={(e) => setFormData({ ...formData, calendarColor: e.target.value.trim() })}
                  helperText="Fondo de las celdas de los lanes de esta categoría en el calendario"
                  InputProps={{
                    endAdornment: formData.calendarColor ? (
                      <InputAdornment position="end">
                        <Button size="small" onClick={() => setFormData({ ...formData, calendarColor: '' })}>Quitar</Button>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <ImageDropUpload
                label="Imagen de la categoría"
                value={formData.imageUrl}
                onChange={(url) => setFormData({ ...formData, imageUrl: url })}
                folder="reservation_categories"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={<Switch checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />}
                label="Active"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={<Switch checked={formData.requiresLicense} onChange={(e) => setFormData({ ...formData, requiresLicense: e.target.checked })} />}
                label="Requires License"
              />
            </Grid>

            {formData.type === 'course' && (
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.privateCourse === true || /privado/i.test(formData.name || '')}
                      disabled={/privado/i.test(formData.name || '')}
                      onChange={(e) => setFormData({ ...formData, privateCourse: e.target.checked })}
                    />
                  }
                  label="Curso privado (pide idioma y nueva/renovación al reservar)"
                />
              </Grid>
            )}

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.hiddenFromPublic === true}
                    onChange={(e) => setFormData({ ...formData, hiddenFromPublic: e.target.checked })}
                  />
                }
                label="Oculto al público (solo admins inscriben)"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                Depósito (pago parcial)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Si está activado, la app solo cobra el depósito al reservar; el resto se paga en el local.
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.depositEnabled}
                    onChange={(e) => setFormData({ ...formData, depositEnabled: e.target.checked })}
                  />
                }
                label="Usar depósito"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Monto del depósito"
                value={(formData.depositCents / 100).toFixed(2)}
                onChange={handlePriceChange('depositCents')}
                disabled={!formData.depositEnabled}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                helperText={
                  formData.depositEnabled
                    ? `Resto a pagar en local`
                    : 'Activa "Usar depósito" para editar'
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Mensaje de balance pendiente"
                value={formData.depositMessage}
                onChange={(e) => setFormData({ ...formData, depositMessage: e.target.value })}
                disabled={!formData.depositEnabled}
                placeholder="Ej: El restante se debe pagar en persona el día de la reservación."
                helperText={formData.depositEnabled ? 'Este mensaje se muestra al cliente cuando hay un balance pendiente. Déjalo vacío para usar el mensaje predeterminado.' : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                Cambios de cita por el cliente
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Hasta cuántas horas antes puede el cliente cambiar su cita él mismo desde "Mis Reservaciones".
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Horas antes para poder cambiar"
                value={formData.rescheduleHoursBefore ?? ''}
                onChange={(e) => setFormData({
                  ...formData,
                  rescheduleHoursBefore: e.target.value === '' ? null : Math.max(0, Number(e.target.value) || 0),
                })}
                placeholder="ej. 4"
                inputProps={{ min: 0 }}
                helperText={
                  formData.rescheduleHoursBefore === null || formData.rescheduleHoursBefore === undefined || formData.rescheduleHoursBefore === ''
                    ? `Vacío = default del sistema (${['course', 'class'].includes(formData.type) ? '24' : '2'} horas para este tipo)`
                    : `El cliente podrá cambiar hasta ${formData.rescheduleHoursBefore} hora${Number(formData.rescheduleHoursBefore) === 1 ? '' : 's'} antes de la cita`
                }
              />
            </Grid>
          </Grid>

          {SCHEDULE_TYPES.includes(formData.type) && (
            <>
              <Divider sx={{ my: 4 }} />
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Horarios recurrentes
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Define los días y horas en que esta categoría está disponible para reservar.
                  Ejemplo: Miércoles y Sábados de 6:00 PM a 7:00 PM.
                </Typography>

                {!editId && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Guarda primero la categoría para poder añadir horarios.
                  </Alert>
                )}

                {editId && (
                  <>
                    {scheduleMsg && (
                      <Alert severity={scheduleMsg.type} sx={{ mb: 2 }} onClose={() => setScheduleMsg(null)}>
                        {scheduleMsg.text}
                      </Alert>
                    )}

                    {/* Horarios existentes */}
                    {pastDatesCount > 0 && (
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <FormControlLabel
                          control={<Switch size="small" checked={hidePastDates} onChange={(e) => setHidePastDates(e.target.checked)} />}
                          label={<Typography variant="body2">Ocultar fechas pasadas ({pastDatesCount})</Typography>}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Las fechas pasadas se conservan como récord; solo se esconden de la lista.
                        </Typography>
                      </Stack>
                    )}
                    <Stack spacing={1} sx={{ mb: 3 }}>
                      {schedules.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Aún no hay horarios. Añade el primero abajo.
                        </Typography>
                      ) : visibleSchedules.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Todas las fechas son pasadas. Desactiva "Ocultar fechas pasadas" para verlas.
                        </Typography>
                      ) : (
                        visibleSchedules.map((s) => (
                          <Stack
                            key={s.id}
                            direction="row"
                            alignItems="center"
                            spacing={2}
                            sx={{
                              p: 1.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              bgcolor: s.active === false ? 'action.disabledBackground' : 'background.paper',
                            }}
                          >
                            <Chip
                              label={s.date ? s.date : (DAYS_SHORT[s.dayOfWeek] || `D${s.dayOfWeek}`)}
                              color={s.date ? 'warning' : 'primary'}
                              size="small"
                              title={s.date ? 'Fecha fija — no se repite' : 'Se repite cada semana'}
                            />
                            {editingScheduleId === s.id ? (
                              <>
                                <TextField
                                  size="small"
                                  label="Inicio"
                                  type="time"
                                  value={editSchedule.startTime}
                                  onChange={(e) => setEditSchedule({ ...editSchedule, startTime: e.target.value })}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ width: 130 }}
                                />
                                <TextField
                                  size="small"
                                  label="Fin"
                                  type="time"
                                  value={editSchedule.endTime}
                                  onChange={(e) => setEditSchedule({ ...editSchedule, endTime: e.target.value })}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ width: 130 }}
                                />
                                <TextField
                                  size="small"
                                  label="Capacidad"
                                  type="number"
                                  value={editSchedule.capacity}
                                  onChange={(e) => setEditSchedule({ ...editSchedule, capacity: e.target.value })}
                                  sx={{ width: 110 }}
                                  inputProps={{ min: 1 }}
                                />
                                <Box sx={{ flex: 1 }} />
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleSaveScheduleEdit(s)}
                                  disabled={editScheduleSaving}
                                >
                                  {editScheduleSaving ? 'Guardando...' : 'Guardar'}
                                </Button>
                                <Button size="small" onClick={() => setEditingScheduleId(null)} disabled={editScheduleSaving}>
                                  Cancelar
                                </Button>
                              </>
                            ) : (
                              <>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                  {s.startTime} – {s.endTime}
                                  {s.maxConcurrentBookings ? ` · cap ${s.maxConcurrentBookings}` : ''}
                                </Typography>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      size="small"
                                      checked={s.active !== false}
                                      onChange={() => handleToggleScheduleActive(s)}
                                    />
                                  }
                                  label={s.active !== false ? 'Activo' : 'Inactivo'}
                                />
                                <IconButton size="small" onClick={() => startEditSchedule(s)} title="Editar horario">
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => handleDeleteSchedule(s.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </Stack>
                        ))
                      )}
                    </Stack>

                    {/* Form añadir nuevo */}
                    <Card variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 2 }}>
                        Añadir nuevo horario
                      </Typography>

                      {/* Recurrente vs. una sola fecha (ej: curso privado) */}
                      <ToggleButtonGroup
                        exclusive
                        value={scheduleMode}
                        onChange={(e, v) => v && setScheduleMode(v)}
                        size="small"
                        sx={{ mb: 2 }}
                      >
                        <ToggleButton value="recurring">Se repite cada semana</ToggleButton>
                        <ToggleButton value="fixed">Una sola fecha</ToggleButton>
                      </ToggleButtonGroup>

                      {scheduleMode === 'fixed' && (
                        <Stack sx={{ mb: 2 }}>
                          <TextField
                            label="Fecha del curso"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={scheduleDate}
                            onChange={(e) => setScheduleDate(e.target.value)}
                            helperText="Este horario solo aplica ese día — no se repite."
                            sx={{ maxWidth: 260 }}
                          />
                        </Stack>
                      )}

                      {scheduleMode === 'recurring' && (
                      <>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Typography variant="body2">Días de la semana</Typography>
                        <ButtonGroup size="small" variant="outlined">
                          <Button onClick={() => setScheduleDays([0, 1, 2, 3, 4, 5, 6])}>Todos</Button>
                          <Button onClick={() => setScheduleDays([1, 2, 3, 4, 5])}>Lun–Vie</Button>
                          <Button onClick={() => setScheduleDays([0, 6])}>Sáb–Dom</Button>
                          <Button onClick={() => setScheduleDays([3, 6])}>Mié+Sáb</Button>
                          <Button onClick={() => setScheduleDays([])}>Limpiar</Button>
                        </ButtonGroup>
                      </Stack>

                      <ToggleButtonGroup
                        value={scheduleDays}
                        onChange={(e, v) => setScheduleDays(v)}
                        size="small"
                        sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}
                      >
                        {DAYS_SHORT.map((d, i) => (
                          <ToggleButton
                            key={i}
                            value={i}
                            sx={{
                              minWidth: 56,
                              borderRadius: '8px !important',
                              border: '1px solid rgba(0,0,0,0.12) !important',
                              '&.Mui-selected': {
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                '&:hover': { bgcolor: 'primary.dark' },
                              },
                            }}
                          >
                            {d}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                      </>
                      )}

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            label="Hora inicio"
                            type="time"
                            value={scheduleStart}
                            onChange={(e) => setScheduleStart(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            label="Hora fin"
                            type="time"
                            value={scheduleEnd}
                            onChange={(e) => setScheduleEnd(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            type="number"
                            label="Capacidad"
                            value={scheduleCapacity}
                            onChange={(e) => setScheduleCapacity(e.target.value)}
                            helperText="Cupos por slot"
                          />
                        </Grid>
                      </Grid>

                      <Button
                        variant="contained"
                        onClick={handleAddSchedule}
                        disabled={scheduleSaving}
                        sx={{ mt: 2 }}
                      >
                        {scheduleSaving ? 'Añadiendo...' : 'Añadir horario'}
                      </Button>
                    </Card>

                    {/* Cierre de lanes durante esta categoría */}
                    <Divider sx={{ my: 4 }} />
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Cerrar lanes durante esta categoría
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Los lanes que marques NO le saldrán disponibles al público en esos
                      días y horas. Ejemplo: cerrar los lanes de rifle mientras se da el curso.
                    </Typography>

                    <Stack spacing={1} sx={{ mb: 3 }}>
                      {closures.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No hay cierres configurados.
                        </Typography>
                      ) : (
                        closures.map((c) => (
                          <Stack
                            key={c.id}
                            direction="row"
                            alignItems="center"
                            spacing={2}
                            sx={{
                              p: 1.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              bgcolor: c.active === false ? 'action.disabledBackground' : 'background.paper',
                            }}
                          >
                            <Chip label={DAYS_SHORT[c.dayOfWeek] || `D${c.dayOfWeek}`} color="error" size="small" />
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {c.startTime} – {c.endTime} · {(c.laneIds || [])
                                .map((id) => {
                                  const lane = allLanes.find((l) => l.id === id);
                                  return lane ? laneLabel(lane, allCategories) : '?';
                                })
                                .join(', ')}
                            </Typography>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={c.active !== false}
                                  onChange={() => handleToggleClosure(c)}
                                />
                              }
                              label={c.active !== false ? 'Activo' : 'Inactivo'}
                            />
                            <IconButton size="small" onClick={() => handleDeleteClosure(c.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        ))
                      )}
                    </Stack>

                    <Card variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 2 }}>
                        Añadir cierre de lanes
                      </Typography>
                      <ToggleButtonGroup
                        value={closureDays}
                        onChange={(e, v) => setClosureDays(v)}
                        size="small"
                        sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}
                      >
                        {DAYS_SHORT.map((d, i) => (
                          <ToggleButton
                            key={i}
                            value={i}
                            sx={{
                              minWidth: 56,
                              borderRadius: '8px !important',
                              border: '1px solid rgba(0,0,0,0.12) !important',
                              '&.Mui-selected': {
                                bgcolor: 'error.main',
                                color: 'error.contrastText',
                                '&:hover': { bgcolor: 'error.dark' },
                              },
                            }}
                          >
                            {d}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            label="Desde"
                            type="time"
                            value={closureStart}
                            onChange={(e) => setClosureStart(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField
                            fullWidth
                            label="Hasta"
                            type="time"
                            value={closureEnd}
                            onChange={(e) => setClosureEnd(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            multiple
                            openOnFocus
                            options={allLanes}
                            getOptionLabel={(l) => laneLabel(l, allCategories)}
                            value={closureLanes}
                            onChange={(e, v) => setClosureLanes(v)}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            renderInput={(params) => (
                              <TextField {...params} label="Lanes a cerrar" placeholder="Escoge lanes" />
                            )}
                          />
                        </Grid>
                      </Grid>

                      <Button
                        variant="contained"
                        color="error"
                        onClick={handleAddClosure}
                        disabled={closureSaving}
                        sx={{ mt: 2 }}
                      >
                        {closureSaving ? 'Guardando...' : 'Añadir cierre'}
                      </Button>
                    </Card>
                  </>
                )}
              </Box>
            </>
          )}

        </Card>

        {/* Fuera del fieldset: un usuario en solo lectura debe poder salir */}
        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          {canEdit && (
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Saving...' : (editId ? 'Update Category' : 'Create Category')}
            </Button>
          )}
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/dashboard/reservation-categories')}
          >
            {canEdit ? 'Cancel' : 'Volver'}
          </Button>
        </Stack>
      </Container>
    </>
  );
}
